import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Twitter, Facebook, Instagram, Linkedin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  BRAND,
  NAV_ITEMS,
  FX_DROPDOWN,
  FUNDED_FORM_DROPDOWN,
  SERVICES_DROPDOWN,
  navItemPath,
  dropdownItemPath,
  servicePath,
} from "../lib/constants";

const QUICK_LINKS = NAV_ITEMS.map((item) => ({
  label: item.label,
  to: navItemPath(item),
}));

const FX_LINKS = FX_DROPDOWN.flatMap((col) =>
  col.items.filter((i) => i.slug).map((i) => ({
    label: i.title,
    to: dropdownItemPath(i),
  }))
).slice(0, 6);

const FUNDED_LINKS = FUNDED_FORM_DROPDOWN.flatMap((col) =>
  col.items.map((i) => ({
    label: i.title,
    to: dropdownItemPath(i),
  }))
);

const SERVICE_LINKS = SERVICES_DROPDOWN.flatMap((col) =>
  col.items.map((i) => ({
    label: i.title,
    to: servicePath(i.slug),
  }))
);

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setDone(true);
    toast.success("Subscribed! Watch your inbox for updates.");
    setEmail("");
    setTimeout(() => setDone(false), 3500);
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative theme-footer pt-20 pb-8 overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[2px] bg-gradient-to-r from-transparent via-[#00C2FF] to-transparent opacity-60" />

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-10">
        <div className="space-y-5 lg:col-span-2">
          <img src={BRAND.logo} alt={BRAND.name} className="h-14 sm:h-20 w-auto max-w-[200px] object-contain object-left" />
          <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
            {BRAND.name} supplies end-to-end broker infrastructure — designed so forex and prop
            businesses can scale without platform bottlenecks.
          </p>
          <div className="flex gap-3">
            {[
              { Icon: Twitter, href: BRAND.socials.twitter, label: "Twitter" },
              { Icon: Facebook, href: BRAND.socials.facebook, label: "Facebook" },
              { Icon: Instagram, href: BRAND.socials.instagram, label: "Instagram" },
              { Icon: Linkedin, href: BRAND.socials.linkedin, label: "LinkedIn" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                data-testid={`footer-${label.toLowerCase()}`}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-gray-400 hover:text-white hover:border-[#00C2FF] hover:bg-[#00C2FF]/10 transition-all"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display theme-heading text-lg font-semibold mb-5">Navigate</h3>
          <ul className="space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  className="text-sm text-gray-400 hover:text-[#00C2FF] transition-colors inline-flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 rounded-full bg-[#9FE3FF] group-hover:w-4 transition-all" />
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display theme-heading text-lg font-semibold mb-5">Platform</h3>
          <ul className="space-y-3">
            {FX_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-gray-400 hover:text-[#00C2FF] transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display theme-heading text-lg font-semibold mb-5">Prop Programs</h3>
          <ul className="space-y-3">
            {FUNDED_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-gray-400 hover:text-[#00C2FF] transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display theme-heading text-lg font-semibold mb-5">Studio</h3>
          <ul className="space-y-3">
            {SERVICE_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-gray-400 hover:text-[#00C2FF] transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:min-w-[11.5rem]">
          <h3 className="font-display theme-heading text-lg font-semibold mb-5">Get in Touch</h3>
          <ul className="space-y-4 text-sm text-gray-400">
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#00C2FF] shrink-0" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-[#00C2FF] transition-colors">
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-center gap-3 min-w-0">
              <Phone className="w-4 h-4 text-[#9FE3FF] shrink-0" />
              <a
                href={`tel:${BRAND.phone.replace(/[\s()-]/g, "")}`}
                className="hover:text-[#9FE3FF] transition-colors whitespace-nowrap shrink-0"
              >
                {BRAND.phone}
              </a>
            </li>
          </ul>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3 mt-6" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@broker.com"
              className="w-full px-4 py-3 rounded-full theme-input border focus:outline-none focus:border-[#00C2FF] text-sm"
              required
            />
            <button type="submit" className="btn-cyan inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm">
              {done ? (<><CheckCircle2 className="w-4 h-4" /> Subscribed</>) : (<>Subscribe <Send className="w-3.5 h-3.5" /></>)}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10 mt-14 pt-6 text-center text-gray-500 text-xs max-w-7xl mx-auto px-6">
        &copy; {new Date().getFullYear()} {BRAND.name}. All rights reserved. Technology vendor — not a broker.
      </div>
    </footer>
  );
}
